import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { RepliedToBroadcastChannel } from '~hooks/useMessaging/models';
import { BROADCAST_ACCENT_WIDTH_IN_PX } from '~pages/Messages/messagingBroadcastAccent';
import { messagingBroadcastTitle } from '~pages/Messages/messagingChannelLabels';

// Enough of the announcement to recognise it without the quote outgrowing the reply.
const EXCERPT_LINE_CLAMP = 2;

type MessageBroadcastQuoteProps = {
  quote: RepliedToBroadcastChannel;
};

export default function MessageBroadcastQuote({ quote }: MessageBroadcastQuoteProps) {
  const theme = useTheme();
  const { excerpt, name, organizerName } = quote;

  const title = messagingBroadcastTitle(name);
  const header = organizerName
    ? t('messaging.broadcast_quote_header_with_organizer', {
        name: title,
        organizer: organizerName,
      })
    : title;

  return (
    <Box
      data-test-id="messaging-broadcast-quote"
      sx={{
        backgroundColor: theme.semantic.surfaceSunken,
        border: `1px solid ${theme.semantic.border}`,
        borderLeft: `${BROADCAST_ACCENT_WIDTH_IN_PX}px solid ${theme.brandV2.colors.treadOrangeDark}`,
        borderRadius: theme.brandV2.borderRadius,
        mb: 0.5,
        px: 1,
        py: 0.75,
      }}
    >
      <Typography
        noWrap
        sx={{ color: theme.semantic.textSecondary, display: 'block' }}
        variant="caption"
      >
        {header}
      </Typography>

      <Typography
        sx={{
          ...(excerpt
            ? {
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: EXCERPT_LINE_CLAMP,
              }
            : { color: theme.semantic.textSecondary, fontStyle: 'italic' }),
          wordBreak: 'break-word',
        }}
        variant="body2"
      >
        {excerpt ?? t('messaging.broadcast_quote_no_excerpt')}
      </Typography>
    </Box>
  );
}
